import type { EmailEvent, EmailExtraction, ExistingBlock, ItemKind, Member, NoteRow, NoticeRow, RoutineRow, Scope, TaskRow } from './types.ts'
import { matchMembers } from './members.ts'
import { addDays, zonedIso } from './dates.ts'

export const MIN_EVENT_CONFIDENCE = 0.75

export interface PlanInput {
  extraction: EmailExtraction
  members: Member[]
  todayYmd: string
  tz: string
  capture: { id: string; user_id: string; subject: string; sender_label: string }
  existing: ExistingBlock[]
}
export interface EventPlan {
  parent: { row: TaskRow } | { existingId: string }
  children: Omit<TaskRow, 'parent_task_id'>[]
}
export interface WritePlan { events: EventPlan[]; inbox: TaskRow[]; routines: RoutineRow[]; note: NoteRow | null; notices: NoticeRow[] }

export function normaliseTitle(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Item titles are re-phrased by the model between runs ("bring payment
 * envelope" vs "payment envelope"), so a retry must not duplicate a child.
 * Containment: shared tokens over the SMALLER title ≥ 0.6.
 */
export function itemsMatch(a: string, b: string): boolean {
  const A = new Set(normaliseTitle(a).split(' ').filter(Boolean))
  const B = new Set(normaliseTitle(b).split(' ').filter(Boolean))
  if (A.size === 0 || B.size === 0) return false
  let inter = 0
  for (const t of A) if (B.has(t)) inter++
  return inter / Math.min(A.size, B.size) >= 0.6
}

/** A mail-born row's audience, as stored. */
export type MailRowAudience = { assigned_to?: string | null; assigned_to_all?: string[] | null }

/** Everyone a mail-born row is for, as a set. */
export function audienceOf(r: MailRowAudience): Set<string> {
  const all = r.assigned_to_all ?? []
  if (all.length) return new Set(all)
  return r.assigned_to ? new Set([r.assigned_to]) : new Set()
}

/**
 * Two mail-born rows with the same words are the same instruction when they
 * are for the same people — which, across two teachers writing to two kids in
 * mirror-image classes, means OVERLAPPING rather than identical audiences.
 * Kaleb's teacher and Ella's teacher each send "return the blue sheet"; the
 * second must attach to the first, not sit beside it. Comparing audiences for
 * EQUALITY is what let both through (2026-09-04).
 *
 * Class-wide rows name nobody at all, and two of those are also the same
 * instruction.
 */
export function sameAudience(a: MailRowAudience, b: MailRowAudience): boolean {
  const A = audienceOf(a)
  const B = audienceOf(b)
  if (A.size === 0 && B.size === 0) return true
  for (const id of B) if (A.has(id)) return true
  return false
}

/** Jaccard overlap of normalised tokens ≥ 0.8. */
export function titlesMatch(a: string, b: string): boolean {
  const A = new Set(normaliseTitle(a).split(' ').filter(Boolean))
  const B = new Set(normaliseTitle(b).split(' ').filter(Boolean))
  if (A.size === 0 || B.size === 0) return false
  let inter = 0
  for (const t of A) if (B.has(t)) inter++
  const union = A.size + B.size - inter
  return inter / union >= 0.8
}

// Mirror of src/lib/scope.ts scopeForDomain — the app's single scope rule.
// (Deno; an edge function cannot import from src/.) Scope is DERIVED from what
// the row IS plus who it was handed to. Nothing may write a literal scope.
export function scopeFor(
  context: string | null | undefined,
  assignees: (string | null | undefined)[],
  self: string | null,
): Scope {
  if (context === 'family') return 'compound'
  return assignees.some((a) => a && a !== self) ? 'couple' : 'individual'
}

// Every row this function writes is household mail: family by construction.
const FAMILY = { context: 'family' as const, scope: scopeFor('family', [], null) }

function sourceNote(c: PlanInput['capture'], quote: string, extra?: string): string {
  const head = `From ${c.sender_label} · ${c.subject}`
  const parts = [head]
  if (extra) parts.push(extra)
  if (quote) parts.push(`“${quote}”`)
  return parts.join('\n\n')
}

function baseRow(i: PlanInput, title: string): TaskRow {
  return {
    user_id: i.capture.user_id, title, completed: false, bucket: 'inbox', ...FAMILY, category: 'task',
    scheduled_for: null, is_all_day: null, location: null, notes: null, capture_id: i.capture.id,
    assigned_to: null, assigned_to_all: null, parent_task_id: null, needed_on: null,
  }
}

const categoryFor = (kind: ItemKind): TaskRow['category'] => (kind === 'homework' ? 'homework' : 'task')

function neededYmd(needed: EmailEvent['items'][number]['needed'], eventYmd: string): string {
  if (needed === 'night_before') return addDays(eventYmd, -1)
  if (needed === 'day_of') return eventYmd
  return needed
}

function childrenFor(i: PlanInput, ev: EmailEvent, skipTitles: string[]): EventPlan['children'] {
  const out: EventPlan['children'] = []
  for (const item of ev.items) {
    const { matched, unmatched } = matchMembers(item.for, i.members)
    const ymd = neededYmd(item.needed, ev.date)
    const push = (title: string, assigned: string | null) => {
      if (skipTitles.some((t) => itemsMatch(t, title))) return
      const { parent_task_id: _omit, ...row } = baseRow(i, title)
      // A child carries notes only when the email said something worth
      // reading when doing it — the kid page expands them under the row.
      out.push({ ...row, assigned_to: assigned, needed_on: ymd, category: categoryFor(item.kind),
        notes: item.detail ? sourceNote(i.capture, '', item.detail) : null })
    }
    for (const m of matched) push(item.text, m.id)
    if (unmatched.length) push(`${item.text} — ${unmatched.join(', ')}`, null)
    if (matched.length === 0 && unmatched.length === 0) push(item.text, null)
  }
  return out
}

export function planWrites(i: PlanInput): WritePlan {
  const events: EventPlan[] = []
  const inbox: TaskRow[] = []
  const routines: RoutineRow[] = []
  const yesterday = addDays(i.todayYmd, -1)

  for (const ev of i.extraction.events) {
    const placeable = ev.confidence >= MIN_EVENT_CONFIDENCE && ev.date >= yesterday
    if (!placeable) {
      const when = ev.time ? `${ev.date} ${ev.time}` : ev.date
      const why = ev.date < yesterday ? `Dated ${when} (already past)` : `Dated ${when} (needs a look — confidence ${ev.confidence.toFixed(2)})`
      const itemLines = ev.items.map((it) => `- ${it.text}${it.for === 'everyone' ? '' : ` (${it.for.join(', ')})`}`).join('\n')
      inbox.push({ ...baseRow(i, ev.title), category: 'event', location: ev.location ?? null,
        notes: sourceNote(i.capture, ev.source_quote, itemLines ? `${why}\n\nItems:\n${itemLines}` : why) })
      continue
    }

    const match = i.existing.find((b) => b.ymd === ev.date && titlesMatch(b.title, ev.title))
    if (match) {
      // Attach only items not already on the existing block. If every item is
      // already there, this event yields no new rows on purpose — the
      // household already has it, and there is nothing new to write.
      const children = childrenFor(i, ev, match.childTitles)
      if (children.length) events.push({ parent: { existingId: match.id }, children })
      continue
    }

    const { matched } = matchMembers(ev.for, i.members)
    const single = ev.for !== 'everyone' && matched.length === 1 ? matched[0].id : null
    const row: TaskRow = {
      ...baseRow(i, ev.title), bucket: 'timed', category: 'event',
      scheduled_for: zonedIso(ev.date, ev.time ?? null, i.tz), is_all_day: !ev.time,
      location: ev.location ?? null, notes: sourceNote(i.capture, ev.source_quote),
      assigned_to: single, assigned_to_all: ev.for === 'everyone' ? matched.map((m) => m.id) : null,
    }
    events.push({ parent: { row }, children: childrenFor(i, ev, []) })
  }

  for (const t of i.extraction.todos) {
    const { matched } = t.for ? matchMembers(t.for, i.members) : { matched: [] as Member[] }
    const row = (assigned: string | null): TaskRow => ({
      ...baseRow(i, t.title), needed_on: t.due ?? null, assigned_to: assigned,
      category: categoryFor(t.kind), notes: sourceNote(i.capture, t.source_quote, t.detail),
    })
    // A STANDING instruction is not a task. "Send the take-home folder daily"
    // as a dated row is wrong twice over: left open it carries forward every
    // day forever, and ticking it off says the household is done sending the
    // folder. It is a routine — it recurs, and each day's copy is its own.
    if (t.repeat) {
      const kids = matched.length ? matched : i.members.filter((m) => m.isChild)
      const single = matched.length === 1 ? matched[0].id : null
      const all = !single && kids.length ? kids.map((k) => k.id) : null
      routines.push({
        user_id: i.capture.user_id,
        name: t.title,
        description: t.detail ?? null,
        recurrence_pattern: t.repeat.days ? { type: t.repeat.type, days: t.repeat.days } : { type: t.repeat.type },
        time_of_day: null,
        visibility: 'active',
        assigned_to: single,
        assigned_to_all: all,
        context: 'family',
        scope: scopeFor('family', [single, ...(all ?? [])], null),
        // Untimed, so Today files it under Unscheduled rather than pinning it
        // to an hour the email never named.
        show_on_timeline: true,
        raw_input: t.source_quote || null,
      })
      continue
    }

    // Homework is a STUDENT's. A class-wide "return the blue sheet" names
    // nobody, and an unassigned homework row sits on the wall's Everyone row
    // where neither kid sees it as theirs (2026-09-03).
    //
    // That was first solved with one row PER child, which put every class-wide
    // instruction on Today once per kid — and with two kids in mirror-image
    // classes, that is every instruction, twice, every day. Worse, it defeated
    // the cross-capture dedupe downstream: that compares assignees, so the
    // same sheet from each kid's teacher looked like two different rows.
    //
    // One row now, carrying every child in `assigned_to_all`. The wall reads
    // it (wallGantt homeworkOwners) and still puts the row on each kid's own
    // track, so nothing the fan-out protected is lost.
    if (t.kind === 'homework' && matched.length !== 1) {
      const kids = matched.length ? matched : i.members.filter((m) => m.isChild)
      if (kids.length) {
        inbox.push({ ...row(null), assigned_to_all: kids.map((k) => k.id) })
        continue
      }
    }
    inbox.push(row(matched.length === 1 ? matched[0].id : null))
  }

  const gtk = i.extraction.good_to_know
  const gaps = i.extraction.gaps
  const note: NoteRow | null = gtk.length || gaps.length
    ? {
        user_id: i.capture.user_id,
        title: `From ${i.capture.sender_label}: ${i.capture.subject}`,
        content: [
          gtk.length ? 'Good to know:\n' + gtk.map((g) => `- ${g.text}`).join('\n') : '',
          gaps.length ? 'Needs another look:\n' + gaps.map((g) => `- ${g.note}`).join('\n') : '',
        ].filter(Boolean).join('\n\n'),
        ...FAMILY, source: 'import', type: 'general', external_id: `capture:${i.capture.id}`,
      }
    : null

  // Standing info → notices, one row per addressee. "everyone" is ONE row
  // with a null member (the wall shows null to every member), not one per
  // kid; a name not on the roster also lands on everyone rather than being
  // guessed onto someone.
  const notices: NoticeRow[] = []
  for (const g of gtk) {
    const row = (member: string | null): NoticeRow => ({
      user_id: i.capture.user_id, family_member_id: member, text: g.text,
      sender_label: i.capture.sender_label, received_on: i.todayYmd, capture_id: i.capture.id,
    })
    if (g.for === 'everyone') { notices.push(row(null)); continue }
    const { matched } = matchMembers(g.for, i.members)
    if (matched.length === 0) { notices.push(row(null)); continue }
    for (const m of matched) notices.push(row(m.id))
  }

  return { events, inbox, routines, note, notices }
}
